import React, { useCallback, useEffect, useMemo, useState } from "react";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../../common/BadgeMultiSelect/BadgeMultiSelect";
import Dropdown from "../../../common/dropDown";
import SegmentDatePicker from "../../../common/aqPickers/SegmentDatePicker";
import SegmentTimePicker from "../../../common/aqPickers/SegmentTimePicker";
import InfoAlert from "../../../common/InfoAlert";
import useInfoAlert from "../../../../hooks/useInfoAlert";
import { openAuthModal } from "../../../common/authmodal/authmodalservice";
import { openESignatureConfirmModal } from "../../../common/esignature/esignatureConfirmService";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import {
  batchOptionLabel,
  findOption,
  formatTxnWhen,
  renderImpProductCell,
  toApiDateTimeString,
  useEpsfLedger,
  useEpsfStudySite,
} from "../epsfLedgerUi";
import { humanizeEpsfCode, renderReceiptStatusBadge } from "../epsfBadges";
import EpsfFormFillModal from "../EpsfFormFillModal";
import {
  checkFormActionBlockers,
  confirmReconcileRequest,
  createReconcileRequest,
  fetchFormWorkItems,
  fetchImpKits,
  fetchImpReconciles,
  formBlockersNeedRequest,
  formBlockMessageForAction,
  linkFormInstance,
  postImpReconcile,
} from "../epsfService";
import ImpQtyKitFloatCell from "./ImpQtyKitFloatCell";
import { createInstance } from "../../esource/esourceService";
import "../EpsfPages.scss";

const STATUS_FILTER_OPTIONS = [
  { value: "Reconciled", label: "Reconciled" },
  { value: "Discrepancy", label: "Discrepancy" },
  { value: "Requested", label: "Requested" },
];

/** Kit statuses included in individual-kit reconciliation (matches SP expected set). */
const RECONCILE_KIT_STATUSES = [
  "Available",
  "Released",
  "Quarantined",
  "Hold",
  "RequiresReview",
  "Returned",
  "InTransit",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function nowTimeHHmm() {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function isReconcileFormTrigger(trigger) {
  const t = String(trigger || "").toUpperCase();
  return t === "RECONCILIATION_STARTED";
}

function workItemStatus(row) {
  return String(
    row?.WorkItemStatus || row?.workItemStatus || row?.Status || row?.status || "",
  ).toUpperCase();
}

function workItemType(row) {
  return String(row?.WorkType || row?.workType || "COMPLETE").toUpperCase();
}

function workflowStatus(row) {
  return String(row?.WorkflowStatus || row?.workflowStatus || "").toUpperCase();
}

function summarizeFormItems(items = []) {
  const list = Array.isArray(items) ? items : [];
  const openFill = list.filter((row) => {
    const st = workItemStatus(row);
    const wt = workItemType(row);
    return st === "OPEN" && (wt === "COMPLETE" || wt === "CORRECT");
  });
  if (openFill.length) return { phase: "fill", items: openFill, label: "Fill form" };
  const openReview = list.filter(
    (row) => workItemStatus(row) === "OPEN" && workItemType(row) === "REVIEW",
  );
  if (openReview.length) {
    return { phase: "approve", items: openReview, label: "Approval pending" };
  }
  const locked = list.filter((row) => {
    const wf = workflowStatus(row);
    const occ = String(row?.OccurrenceStatus || row?.occurrenceStatus || "").toUpperCase();
    return wf === "APPROVED" || wf === "COMPLETED" || occ === "COMPLETE" || occ === "COMPLETED";
  });
  if (locked.length) {
    return {
      phase: "locked",
      items: [locked[0]],
      label: workflowStatus(locked[0]) === "APPROVED" ? "Approved" : "Completed",
    };
  }
  return { phase: "none", items: [], label: "" };
}

const HISTORY_FIELD_OPTIONS = [
  { key: "ReconciledAt", label: "Reconciled" },
  { key: "BatchNumber", label: "Batch / Lot" },
  { key: "PhysicalCount", label: "Available" },
  { key: "Difference", label: "Difference" },
  { key: "FormDue", label: "Form" },
  { key: "Status", label: "Status" },
  { key: "ExpectedBalance", label: "Expected" },
  { key: "Product", label: "IMP" },
  { key: "ReconciledByName", label: "Reconciled by" },
];

const HISTORY_DEFAULT_COLUMNS = [
  "ReconciledAt",
  "BatchNumber",
  "PhysicalCount",
  "Difference",
  "FormDue",
  "Status",
];

/** Create request creates RECONCILIATION_STARTED form due (Form column + COMPLETE_RECONCILIATION gate). */
const SHOW_RECONCILE_CREATE_REQUEST = true;

const emptyForm = () => ({
  impBatchId: "",
  selectedKitNumbers: [],
  txnDate: new Date(),
  txnTime: nowTimeHHmm(),
  comment: "",
});

export default function ImpAccountability({
  overlay = false,
  preferImpProductId = null,
  preferImpBatchId = null,
  onOverlayClose = null,
  onOverlaySuccess = null,
} = {}) {
  const { loading, canView, moduleAccess, canReconcile } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const {
    balances,
    error,
    setError,
    refresh: refreshBalances,
    loading: balancesLoading,
  } = useEpsfLedger(ctx.studyId, ctx.siteId, null);

  const preferProductKey = String(preferImpProductId || "").toLowerCase();
  const preferBatchId = String(preferImpBatchId || "").trim();

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [formBlocked, setFormBlocked] = useState(false);
  const [formBlockMessage, setFormBlockMessage] = useState("");
  const [formGateBusy, setFormGateBusy] = useState(false);
  const [needsRequest, setNeedsRequest] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [siteKits, setSiteKits] = useState([]);
  const [listKits, setListKits] = useState([]);
  const [kitsLoading, setKitsLoading] = useState(false);
  const [formDueById, setFormDueById] = useState({});
  const [formDueTick, setFormDueTick] = useState(0);
  const [formFillBusy, setFormFillBusy] = useState(false);
  const [formFillModal, setFormFillModal] = useState(null);
  const [statusFilters, setStatusFilters] = useState([]);

  const refreshFormDues = useCallback(() => {
    setFormDueTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId) {
        setListKits([]);
        return;
      }
      try {
        const result = await fetchImpKits(ctx.studyId, ctx.siteId, {});
        if (cancelled) return;
        setListKits(result.success ? result.rows || [] : []);
      } catch {
        if (!cancelled) setListKits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.studyId, ctx.siteId, history]);

  const kitsByBatchId = useMemo(() => {
    const map = new Map();
    (listKits || []).forEach((k) => {
      const status = String(k.Status || k.status || "");
      if (!RECONCILE_KIT_STATUSES.includes(status)) return;
      const batchId = String(k.ImpBatchId || k.impBatchId || "")
        .trim()
        .toLowerCase();
      const kitNo = String(k.KitNumber || k.kitNumber || "").trim();
      if (!batchId || !kitNo) return;
      if (!map.has(batchId)) map.set(batchId, []);
      map.get(batchId).push(kitNo);
    });
    map.forEach((list, key) => {
      map.set(
        key,
        [...new Set(list)].sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true }),
        ),
      );
    });
    return map;
  }, [listKits]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId) {
        setFormDueById({});
        return;
      }
      try {
        const work = await fetchFormWorkItems(ctx.studyId, ctx.siteId, "*");
        if (cancelled) return;
        const map = {};
        (work.rows || []).forEach((row) => {
          const id = String(row.LinkedTxnId || row.linkedTxnId || "").toLowerCase();
          if (!id || !isReconcileFormTrigger(row.TriggerCode || row.triggerCode)) return;
          if (!map[id]) map[id] = [];
          map[id].push(row);
        });
        setFormDueById(map);
      } catch {
        if (!cancelled) setFormDueById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.studyId, ctx.siteId, history, formDueTick]);

  const historyRows = useMemo(
    () =>
      (history || []).map((row) => {
        const id = String(row.ReconcileId || row.reconcileId || "").toLowerCase();
        const dues = formDueById[id] || [];
        const summary = summarizeFormItems(dues);
        const batchId = String(row.ImpBatchId || row.impBatchId || "")
          .trim()
          .toLowerCase();
        const fallbackKits = kitsByBatchId.get(batchId) || [];
        const fallbackCsv = fallbackKits.length ? fallbackKits.join(",") : "";
        const kitNumbers = String(row.KitNumbers || row.kitNumbers || "").trim();
        const expectedKits = String(
          row.ExpectedKitNumbers || row.expectedKitNumbers || "",
        ).trim();
        const physicalKits = String(
          row.PhysicalKitNumbers || row.physicalKitNumbers || "",
        ).trim();
        const discrepancyKits = String(
          row.DiscrepancyKitDetails || row.discrepancyKitDetails || "",
        ).trim();
        /* Derive missing/unexpected when API returned both kit sets but no discrepancy field. */
        let derivedDiscrepancy = discrepancyKits;
        if (!derivedDiscrepancy && expectedKits && physicalKits) {
          const expSet = new Set(
            expectedKits.split(/[,;]+/).map((x) => x.trim()).filter(Boolean),
          );
          const physSet = new Set(
            physicalKits.split(/[,;]+/).map((x) => x.trim()).filter(Boolean),
          );
          const parts = [];
          expSet.forEach((kit) => {
            if (!physSet.has(kit)) parts.push(`${kit}|MISSING`);
          });
          physSet.forEach((kit) => {
            if (!expSet.has(kit)) parts.push(`${kit}|UNEXPECTED`);
          });
          derivedDiscrepancy = parts.length ? parts.join(",") : "";
        }
        return {
          ...row,
          FormDuePhase: summary.phase,
          FormDueLabel: summary.label,
          FormDueItems: summary.items,
          FormDue: summary.label || "",
          /* Batch click: stored kits, else current batch kits. */
          KitNumbers: kitNumbers || fallbackCsv || null,
          /* Expected / Physical: prefer stored set; else batch kits (legacy qty rows). */
          ExpectedKitNumbers: expectedKits || kitNumbers || fallbackCsv || null,
          PhysicalKitNumbers: physicalKits || kitNumbers || fallbackCsv || null,
          /* Difference: only real missing/unexpected — never invent from batch fallback. */
          DiscrepancyKitDetails: derivedDiscrepancy || null,
        };
      }),
    [history, formDueById, kitsByBatchId],
  );

  const filteredHistoryRows = useMemo(() => {
    if (!statusFilters.length) return historyRows;
    const selected = new Set(statusFilters.map((v) => String(v || "")));
    return (historyRows || []).filter((row) =>
      selected.has(String(row.Status || row.status || "")),
    );
  }, [historyRows, statusFilters]);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "Status") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={STATUS_FILTER_OPTIONS}
            value={statusFilters}
            onChange={setStatusFilters}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter status"
            popoverSubtitle="Select one or more statuses"
            showSelectAll
            compactSummary
            disabled={options?.disabled}
          />
        );
      }
      return null;
    },
    [statusFilters],
  );

  const loadHistory = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    try {
      const result = await fetchImpReconciles(ctx.studyId, ctx.siteId);
      if (result.success) setHistory(result.rows || []);
      else if (result.message) setError(result.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [ctx.studyId, ctx.siteId, setError]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onRefresh = async () => {
    await refreshBalances();
    await loadHistory();
  };

  const batchOptions = useMemo(
    () =>
      (balances || [])
        .filter((b) => {
          if (!preferProductKey) return true;
          return (
            String(b.ImpProductId || b.impProductId || "").toLowerCase() ===
            preferProductKey
          );
        })
        .filter((b) => {
          const method = String(
            b.InventoryTrackingMethod || b.inventoryTrackingMethod || "",
          );
          return method === "IndividualKit";
        })
        .map((b) => ({
          value: String(b.ImpBatchId || b.impBatchId),
          label: batchOptionLabel(b),
          row: b,
        })),
    [balances, preferProductKey],
  );

  const batchOptionsWithPlaceholder = useMemo(
    () => [{ value: "", label: "Select batch" }, ...batchOptions],
    [batchOptions],
  );

  useEffect(() => {
    if (!showModal || !ctx.studyId || !ctx.siteId) {
      if (!showModal) setSiteKits([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setKitsLoading(true);
      try {
        const result = await fetchImpKits(ctx.studyId, ctx.siteId, {
          impProductId: preferImpProductId || null,
          impBatchId: form.impBatchId || null,
        });
        if (cancelled) return;
        if (result.success) setSiteKits(result.rows || []);
        else setSiteKits([]);
      } catch {
        if (!cancelled) setSiteKits([]);
      } finally {
        if (!cancelled) setKitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showModal,
    ctx.studyId,
    ctx.siteId,
    preferImpProductId,
    form.impBatchId,
  ]);

  const reconcileKitGroups = useMemo(() => {
    const batchId = String(form.impBatchId || "").trim();
    if (!batchId) return [];

    const batchMeta = new Map();
    (balances || []).forEach((b) => {
      const id = String(b.ImpBatchId || b.impBatchId || "");
      if (!id || id.toLowerCase() !== batchId.toLowerCase()) return;
      batchMeta.set(id.toLowerCase(), {
        batchId: id,
        productCode:
          b.ProductCode || b.productCode || b.ProductName || b.productName || "",
        batchNumber: b.BatchNumber || b.batchNumber || "",
      });
    });

    const byBatch = new Map();
    (siteKits || []).forEach((k) => {
      const status = String(k.Status || k.status || "");
      if (!RECONCILE_KIT_STATUSES.includes(status)) return;
      const kidBatch = String(k.ImpBatchId || k.impBatchId || "");
      if (
        !kidBatch ||
        kidBatch.toLowerCase() !== batchId.toLowerCase()
      ) {
        return;
      }
      const key = kidBatch.toLowerCase();
      if (!byBatch.has(key)) {
        const meta = batchMeta.get(key) || {
          batchId: kidBatch,
          productCode: k.ProductCode || k.productCode || "",
          batchNumber: k.BatchNumber || k.batchNumber || "",
        };
        byBatch.set(key, { ...meta, kits: [] });
      }
      byBatch.get(key).kits.push({
        ImpKitId: String(k.ImpKitId || k.impKitId || ""),
        KitNumber: String(k.KitNumber || k.kitNumber || "").trim(),
        Status: status,
        ImpBatchId: kidBatch,
      });
    });

    return Array.from(byBatch.values())
      .map((g) => ({
        ...g,
        kits: g.kits
          .filter((k) => k.KitNumber)
          .sort((a, b) =>
            String(a.KitNumber).localeCompare(String(b.KitNumber), undefined, {
              numeric: true,
            }),
          ),
      }))
      .filter((g) => g.kits.length);
  }, [siteKits, balances, form.impBatchId]);

  const selectedBatchKits = useMemo(
    () => reconcileKitGroups[0]?.kits || [],
    [reconcileKitGroups],
  );

  const allReconcileKitNumbers = useMemo(
    () => selectedBatchKits.map((k) => k.KitNumber).filter(Boolean),
    [selectedBatchKits],
  );

  const expectedCount = allReconcileKitNumbers.length;
  const physicalCount = form.selectedKitNumbers.length;
  const difference = physicalCount - expectedCount;

  const confirmRequestedReconcile = useCallback(
    (row) => {
      const id = String(row?.ReconcileId || row?.reconcileId || "").trim();
      if (!id || busy) return;

      showAlert({
        title: "Confirm reconcile",
        message:
          "Are you sure you want to confirm this reconcile request? You must sign and authenticate to complete this action.",
        type: "warning",
        buttonText: "Continue",
        cancelText: "Cancel",
        onConfirm: async () => {
          hideAlert();
          const sign = await openESignatureConfirmModal({
            title: "Sign reconcile",
          });
          if (
            !sign ||
            sign.cancelled ||
            Number(sign.Success ?? sign.success ?? 0) !== 1 ||
            !sign.SignatureBase64
          ) {
            return;
          }

          const auth = await openAuthModal({
            introMessage:
              "Re-authenticate to confirm this IMP reconcile. Reconcile notes are required.",
            showDescription: true,
            requireDescription: true,
            descriptionLabel: "Reconcile notes",
            descriptionPlaceholder: "Enter notes for this reconcile…",
            descriptionMaxLength: 1000,
          });
          if (
            !auth ||
            auth.cancelled ||
            Number(auth.Success ?? auth.success ?? 0) !== 1
          ) {
            return;
          }
          const notes = String(auth.Description || auth.description || "").trim();
          if (!notes) {
            showAlert({
              title: "Reconcile notes required",
              message: "Enter reconcile notes to confirm this reconcile.",
              type: "danger",
              buttonText: "OK",
            });
            return;
          }

          setBusy(true);
          try {
            const result = await confirmReconcileRequest({
              StudyId: ctx.studyId,
              SiteId: ctx.siteId,
              ImpTxnId: id,
              Quantity: row.PhysicalCount ?? row.physicalCount,
              Notes: notes,
            });
            if (!result.success) {
              showAlert({
                title: "Confirm reconcile failed",
                message: result.message || "Confirm reconcile failed.",
                type: "danger",
                buttonText: "OK",
              });
              return;
            }
            await onRefresh();
            refreshFormDues();
            showAlert({
              title: "Reconcile confirmed",
              message: result.message || "Reconcile request was completed successfully.",
              type: "success",
              buttonText: "OK",
            });
          } finally {
            setBusy(false);
          }
        },
      });
    },
    [
      busy,
      ctx.siteId,
      ctx.studyId,
      hideAlert,
      onRefresh,
      refreshFormDues,
      showAlert,
    ],
  );

  const renderCell = useCallback((row, field) => {
    if (field === "FormDue") {
      const phase = row.FormDuePhase || "none";
      if (phase === "none") return "—";
      const label = row.FormDueLabel || "Fill form";
      const toneClass =
        phase === "fill"
          ? "epsf-receipt-form-due--fill"
          : phase === "approve"
            ? "epsf-receipt-form-due--approve"
            : "epsf-receipt-form-due--locked";
      return (
        <button
          type="button"
          className={`epsf-receipt-form-due epsf-receipt-form-due--action ${toneClass}`}
          disabled={formFillBusy}
          onClick={async (e) => {
            e.stopPropagation();
            const workItem = (row.FormDueItems || [])[0];
            if (!workItem) return;
            setFormFillBusy(true);
            try {
              let instanceId =
                workItem.FormInstanceId || workItem.formInstanceId || null;
              if (!instanceId && phase === "fill") {
                const templateId =
                  workItem.FormTemplateId || workItem.formTemplateId;
                const occurrenceId =
                  workItem.FormOccurrenceId || workItem.formOccurrenceId;
                const created = await createInstance({
                  FormTemplateId: templateId,
                  StudyId: ctx.studyId,
                  SiteId: ctx.siteId,
                  Title: workItem.Title || workItem.title || "Pharmacy form",
                });
                if (!created.success || !created.formInstanceId) return;
                instanceId = created.formInstanceId;
                await linkFormInstance({
                  FormOccurrenceId: occurrenceId,
                  FormInstanceId: instanceId,
                });
                refreshFormDues();
              }
              if (!instanceId) return;
              setFormFillModal({
                formInstanceId: instanceId,
                workItem: {
                  ...workItem,
                  FormInstanceId: instanceId,
                  LinkedTxnId:
                    workItem.LinkedTxnId ||
                    workItem.linkedTxnId ||
                    null,
                  StudyId: workItem.StudyId || ctx.studyId,
                  SiteId: workItem.SiteId || ctx.siteId,
                },
                mode:
                  phase === "approve"
                    ? "review"
                    : phase === "locked"
                      ? "locked"
                      : "fill",
              });
            } finally {
              setFormFillBusy(false);
            }
          }}
        >
          {label}
        </button>
      );
    }
    if (field === "ReconciledAt") {
      return formatTxnWhen({ TxnAt: row.ReconciledAt || row.reconciledAt }) || "—";
    }
    if (field === "Product") {
      return renderImpProductCell(row);
    }
    if (field === "BatchNumber") {
      const batch = row.BatchNumber || row.batchNumber || "—";
      const expected = row.ExpectedBalance ?? row.expectedBalance;
      const display =
        expected == null || expected === ""
          ? batch
          : `${batch} (${expected})`;
      return (
        <ImpQtyKitFloatCell
          row={row}
          display={display}
          kitField="KitNumbers"
          floatTitle={`Kits · ${batch}`}
          allowEmptyClick
        />
      );
    }
    if (field === "ExpectedBalance") {
      const value = row.ExpectedBalance ?? row.expectedBalance;
      return value == null || value === "" ? "—" : String(value);
    }
    if (field === "PhysicalCount") {
      const value = row.PhysicalCount ?? row.physicalCount;
      const label = value == null || value === "" ? "—" : String(value);
      return (
        <ImpQtyKitFloatCell
          row={row}
          display={label}
          kitField="PhysicalKitNumbers"
          floatTitle="Available kits"
          allowEmptyClick
        />
      );
    }
    if (field === "Difference") {
      const value = row.Difference ?? row.difference;
      const label = value == null || value === "" ? "—" : String(value);
      const diffNum = Number(value);
      const hasDiff = Number.isFinite(diffNum) && diffNum !== 0;
      const exp = row.ExpectedBalance ?? row.expectedBalance;
      const phys = row.PhysicalCount ?? row.physicalCount;
      const emptyHint = hasDiff
        ? `Quantity discrepancy: expected ${exp ?? "—"}, physical ${phys ?? "—"} (${label}). Kit identities were not saved for this reconcile — run a kit reconcile to capture Missing / Unexpected kits.`
        : "No kit discrepancy.";
      return (
        <ImpQtyKitFloatCell
          row={row}
          display={label}
          kitField="DiscrepancyKitDetails"
          floatTitle="Discrepancy kits"
          allowEmptyClick={hasDiff || label !== "—"}
          emptyMessage={emptyHint}
        />
      );
    }
    if (field === "Status") {
      const st = String(row.Status || row.status || "");
      if (st === "Requested") {
        const canConfirm =
          row.FormDuePhase === "locked" || row.FormDuePhase === "none";
        return (
          <span className="d-inline-flex flex-column gap-1 align-items-start">
            {renderReceiptStatusBadge("Requested")}
            {canConfirm ? (
              <button
                type="button"
                className="report-table__cell-link"
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  confirmRequestedReconcile(row);
                }}
              >
                Confirm reconcile
              </button>
            ) : null}
          </span>
        );
      }
      if (st) {
        return (
          <span className="epsf-reconcile-status">
            {renderReceiptStatusBadge(st)}
          </span>
        );
      }
      return humanizeEpsfCode(st) || "—";
    }
    if (field === "ReconciledByName") {
      return row.ReconciledByName || row.reconciledByName || "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, [busy, confirmRequestedReconcile, formFillBusy, ctx.siteId, ctx.studyId, refreshFormDues]);

  const openAdd = () => {
    setForm({
      ...emptyForm(),
      impBatchId: preferBatchId || "",
    });
    setFormError("");
    setError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    setShowModal(true);
  };

  const closeModal = () => {
    if (busy || requestBusy) return;
    setShowModal(false);
    setFormError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    if (overlay) onOverlayClose?.();
  };

  const refreshReconcileFormGate = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return;
    }
    setFormGateBusy(true);
    try {
      const res = await checkFormActionBlockers(
        ctx.studyId,
        ctx.siteId,
        "COMPLETE_RECONCILIATION",
      );
      const message = formBlockMessageForAction(res, "Save reconciliation");
      const needReq = formBlockersNeedRequest(res);
      if (res.success && message) {
        setFormBlocked(true);
        setFormBlockMessage(message);
        setNeedsRequest(needReq);
      } else if (!res.success) {
        setFormBlocked(true);
        setFormBlockMessage(
          res.message ||
            "Could not verify reconciliation form requirements. Save is blocked until the check succeeds.",
        );
        setNeedsRequest(false);
      } else {
        setFormBlocked(false);
        setFormBlockMessage("");
        setNeedsRequest(false);
      }
    } catch {
      setFormBlocked(true);
      setFormBlockMessage(
        "Could not verify reconciliation form requirements. Save is blocked until the check succeeds.",
      );
      setNeedsRequest(false);
    } finally {
      setFormGateBusy(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  /* Gate Save only — Create request creates the due form. */
  useEffect(() => {
    if (!showModal) {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      await refreshReconcileFormGate();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, refreshReconcileFormGate]);

  /* Prefer batch from inventory overlay. */
  useEffect(() => {
    if (!showModal || !preferBatchId) return;
    setForm((prev) =>
      prev.impBatchId ? prev : { ...prev, impBatchId: preferBatchId },
    );
  }, [showModal, preferBatchId]);

  const toggleKitNumber = (kitNumber) => {
    const no = String(kitNumber || "").trim();
    if (!no) return;
    setForm((prev) => {
      const set = new Set(prev.selectedKitNumbers);
      if (set.has(no)) set.delete(no);
      else set.add(no);
      return { ...prev, selectedKitNumbers: Array.from(set) };
    });
  };

  const toggleSelectAllKits = () => {
    const nums = selectedBatchKits
      .map((k) => String(k.KitNumber || "").trim())
      .filter(Boolean);
    if (!nums.length) return;
    setForm((prev) => {
      const allOn = nums.every((n) => prev.selectedKitNumbers.includes(n));
      return {
        ...prev,
        selectedKitNumbers: allOn ? [] : nums,
      };
    });
  };

  const primaryBatchIdForRequest = String(form.impBatchId || "").trim();

  const onCreateRequest = async () => {
    setFormError("");
    if (!canReconcile && !canView) {
      setFormError("You do not have permission to reconcile IMP.");
      return;
    }
    if (!ctx.studyId || !ctx.siteId) {
      setFormError("Select study and site.");
      return;
    }
    if (!primaryBatchIdForRequest) {
      setFormError("Select a batch. Reconcile one batch at a time.");
      return;
    }
    if (!form.selectedKitNumbers.length) {
      setFormError("Select the physical kits observed.");
      return;
    }
    setRequestBusy(true);
    try {
      const result = await createReconcileRequest({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: primaryBatchIdForRequest,
        Quantity: form.selectedKitNumbers.length,
        KitNumbers: JSON.stringify(form.selectedKitNumbers),
        TxnAt: toApiDateTimeString(form.txnDate, form.txnTime),
        Notes: form.comment.trim(),
      });
      if (!result.success) {
        setFormError(result.message || "Could not create reconciliation request.");
        return;
      }
      setShowModal(false);
      setForm(emptyForm());
      await onRefresh();
      refreshFormDues();
      if (overlay) onOverlaySuccess?.();
    } finally {
      setRequestBusy(false);
    }
  };

  useEffect(() => {
    if (!overlay) return undefined;
    openAdd();
    return undefined;
    // Mount-once for overlay launch from Inventory.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!canReconcile && !canView) {
      setFormError("You do not have permission to reconcile IMP.");
      return;
    }
    if (formBlocked || formGateBusy) {
      setFormError(
        formBlockMessage ||
          "Save reconciliation is blocked until the required form is completed and approved. Use Create request first.",
      );
      return;
    }
    if (!ctx.studyId || !ctx.siteId) {
      setFormError("Select study and site.");
      return;
    }
    if (!form.impBatchId) {
      setFormError("Select a batch. Reconcile one batch at a time.");
      return;
    }
    if (!form.selectedKitNumbers.length) {
      setFormError("Select the physical kits observed.");
      return;
    }

    const qty = form.selectedKitNumbers.length;
    const countLabel = `${qty} kit${qty === 1 ? "" : "s"}`;

    showAlert({
      title: "Confirm reconcile",
      message: `Are you sure you want to reconcile ${countLabel}? You must sign and authenticate to complete this action.`,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const sign = await openESignatureConfirmModal({
          title: "Sign reconcile",
        });
        if (
          !sign ||
          sign.cancelled ||
          Number(sign.Success ?? sign.success ?? 0) !== 1 ||
          !sign.SignatureBase64
        ) {
          return;
        }

        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to post this IMP reconcile. Reconcile notes are required.",
          showDescription: true,
          requireDescription: true,
          descriptionLabel: "Reconcile notes",
          descriptionPlaceholder: "Enter notes for this reconcile…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const notes = String(auth.Description || auth.description || "").trim();
        if (!notes) {
          setFormError("Reconcile notes are required.");
          return;
        }

        setBusy(true);
        try {
          const result = await postImpReconcile({
            StudyId: ctx.studyId,
            SiteId: ctx.siteId,
            ImpBatchId: form.impBatchId,
            KitNumbers: JSON.stringify(form.selectedKitNumbers),
            TxnAt: toApiDateTimeString(form.txnDate, form.txnTime),
            Notes: notes,
          });
          if (!result.success) {
            setFormError(result.message || "Kit reconcile failed.");
            return;
          }
          const detail = (result.rows || [])
            .map(
              (r) =>
                `${r.MatchResult || r.matchResult}: ${r.KitNumber || r.kitNumber}`,
            )
            .filter(Boolean)
            .slice(0, 12);
          showAlert({
            title: "Reconcile recorded",
            message: `${result.message || "Kit reconciliation recorded."}${
              detail.length ? `\n\n${detail.join("\n")}` : ""
            }${(result.rows || []).length > 12 ? "\n…" : ""}`,
            type: "success",
            buttonText: "OK",
          });
          setShowModal(false);
          setForm(emptyForm());
          await onRefresh();
          refreshFormDues();
          if (overlay) onOverlaySuccess?.();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      {!overlay ? (
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: "Reconcile" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: onRefresh },
            {
              icon: "fas fa-plus",
              label: "Reconcile",
              onClick: openAdd,
            },
          ]}
        />

        {error ? <p className="epsf-list-page__error">{error}</p> : null}

        <div className="epsf-reconcile-page">
          <div className="epsf-report-wrap">
            <ReportTable
              key="epsf-imp-reconcile-history-v12"
              rows={!ctx.studyId || !ctx.siteId ? [] : filteredHistoryRows}
              loading={historyLoading || balancesLoading}
              fieldOptions={HISTORY_FIELD_OPTIONS}
              defaultColumns={HISTORY_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                HISTORY_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                field
              }
              renderCell={renderCell}
              renderColumnSearch={renderColumnSearch}
              getRowKey={(row, index) =>
                String(row.ReconcileId || row.reconcileId || index)
              }
              persistLayoutKey="epsf-imp-reconcile-history-v12"
              emptyMessage={
                !ctx.studyId || !ctx.siteId
                  ? "Select study and site in the breadcrumb bar."
                  : "No reconciliations recorded yet."
              }
              pageSizeOptions={[10, 25, 50, 100]}
              columnSearchMode="client"
            />
          </div>
        </div>
      </div>
      ) : null}

        {showModal ? (
          <div
            className="modal fade show d-block epsf-modal epsf-reconcile-modal"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-xl epsf-reconcile-modal__dialog">
              <div className="modal-content epsf-reconcile-modal__content">
                <form
                  className="epsf-reconcile-modal__form"
                  onSubmit={onSubmit}
                >
                  <div className="modal-header">
                    <h5 className="modal-title mb-0">Reconcile</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeModal}
                      disabled={busy}
                    />
                  </div>
                  <div className="modal-body epsf-reconcile-modal__body">
                    <div className="row g-3 epsf-reconcile-modal__layout">
                      <div className="col-12 col-lg-5 epsf-reconcile-modal__meta">
                      <div className="epsf-reconcile-modal__summary">
                        <div>
                          <span className="epsf-reconcile-modal__summary-label">
                            Expected
                          </span>
                          <strong>{expectedCount}</strong>
                        </div>
                        <div>
                          <span className="epsf-reconcile-modal__summary-label">
                            Physical
                          </span>
                          <strong>{physicalCount}</strong>
                        </div>
                        <div>
                          <span className="epsf-reconcile-modal__summary-label">
                            Difference
                          </span>
                          <strong
                            className={
                              difference === 0
                                ? "epsf-reconcile-modal__diff--ok"
                                : "epsf-reconcile-modal__diff--warn"
                            }
                          >
                            {difference}
                          </strong>
                        </div>
                      </div>
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Date <span className="mandatory">*</span>
                            </label>
                            <SegmentDatePicker
                              selected={form.txnDate}
                              onChange={(d) =>
                                setForm((prev) => ({ ...prev, txnDate: d }))
                              }
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Time <span className="mandatory">*</span>
                            </label>
                            <SegmentTimePicker
                              value={form.txnTime}
                              onChange={(t) =>
                                setForm((prev) => ({ ...prev, txnTime: t }))
                              }
                            />
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="epsf-form-field">
                            <label className="form-label">Comment</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={form.comment}
                              onChange={(ev) =>
                                setForm((prev) => ({
                                  ...prev,
                                  comment: ev.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                      {difference !== 0 && form.impBatchId ? (
                        <p className="epsf-list-page__error mt-2 mb-0">
                          Discrepancy: missing kits will move to Quarantine.
                          Ledger balance will not change. Release only after a
                          correct reconcile of this batch.
                        </p>
                      ) : physicalCount > 0 && difference === 0 ? (
                        <p className="epsf-list-page__ok mt-2 mb-0">
                          Physical kits match expected. Quarantined kits on this
                          batch that are observed will be released to Available.
                        </p>
                      ) : null}
                      {formBlockMessage ? (
                        <p className="epsf-list-page__error mt-2 mb-0">
                          {formBlockMessage}
                        </p>
                      ) : null}
                      {formError ? (
                        <p className="epsf-list-page__error mt-2 mb-0">
                          {formError}
                        </p>
                      ) : null}
                      </div>

                      <div className="col-12 col-lg-7 epsf-reconcile-modal__kits">
                      <div className="epsf-return-kits-panel epsf-reconcile-modal__kits-panel">
                        <div className="epsf-form-field mb-2">
                          <label className="form-label">
                            Batch / Lot <span className="mandatory">*</span>
                          </label>
                          <Dropdown
                            options={batchOptionsWithPlaceholder}
                            selected={findOption(
                              batchOptionsWithPlaceholder,
                              form.impBatchId,
                            )}
                            onSelect={(opt) =>
                              setForm((prev) => ({
                                ...prev,
                                impBatchId: opt?.value || "",
                                selectedKitNumbers: [],
                              }))
                            }
                            showSearch
                          />
                        </div>
                        <div className="epsf-return-kits-panel__head">
                          <h6 className="epsf-return-kits-panel__title">
                            Physical kits observed
                          </h6>
                          {form.impBatchId && selectedBatchKits.length ? (
                            <span className="epsf-return-kits-panel__count">
                              {physicalCount} / {expectedCount} selected
                            </span>
                          ) : null}
                        </div>
                        {kitsLoading ? (
                          <p className="epsf-return-kits-panel__empty">
                            Loading kits…
                          </p>
                        ) : !form.impBatchId ? (
                          <p className="epsf-return-kits-panel__empty">
                            Select a batch to see its kits.
                          </p>
                        ) : selectedBatchKits.length ? (
                          <>
                            <label className="epsf-reconcile-modal__select-all">
                              <input
                                type="checkbox"
                                checked={
                                  selectedBatchKits.length > 0 &&
                                  selectedBatchKits.every((k) =>
                                    form.selectedKitNumbers.includes(
                                      k.KitNumber,
                                    ),
                                  )
                                }
                                ref={(el) => {
                                  if (!el) return;
                                  const some =
                                    form.selectedKitNumbers.length > 0 &&
                                    !selectedBatchKits.every((k) =>
                                      form.selectedKitNumbers.includes(
                                        k.KitNumber,
                                      ),
                                    );
                                  el.indeterminate = some;
                                }}
                                onChange={toggleSelectAllKits}
                              />
                              <span>Select all kits</span>
                            </label>
                            <ul
                              className="epsf-return-kits-panel__list epsf-reconcile-modal__kit-list"
                              aria-label="Kits to reconcile"
                            >
                              {selectedBatchKits.map((k) => {
                                const checked =
                                  form.selectedKitNumbers.includes(
                                    k.KitNumber,
                                  );
                                return (
                                  <li key={k.ImpKitId || k.KitNumber}>
                                    <label
                                      className={`epsf-return-kits-panel__item epsf-return-kits-panel__item--flat${
                                        checked
                                          ? " epsf-return-kits-panel__item--selected"
                                          : ""
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                          toggleKitNumber(k.KitNumber)
                                        }
                                      />
                                      <span className="epsf-return-kits-panel__kit-no">
                                        {k.KitNumber}
                                      </span>
                                      {k.Status
                                        ? renderReceiptStatusBadge(k.Status)
                                        : null}
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        ) : (
                          <p className="epsf-return-kits-panel__empty">
                            No accountable kits on this batch.
                          </p>
                        )}
                      </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                      disabled={busy || requestBusy}
                    >
                      Cancel
                    </button>
                    {SHOW_RECONCILE_CREATE_REQUEST ? (
                      <button
                        type="button"
                        className={
                          needsRequest || formBlocked
                            ? "btn btn-primary"
                            : "btn btn-outline-primary"
                        }
                        onClick={onCreateRequest}
                        disabled={busy || requestBusy || formGateBusy}
                      >
                        {requestBusy ? "Creating..." : "Create request"}
                      </button>
                    ) : null}
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={
                        busy ||
                        requestBusy ||
                        formGateBusy ||
                        formBlocked
                      }
                    >
                      {busy
                        ? "Saving..."
                        : formGateBusy
                          ? "Checking forms..."
                          : "Save reconciliation"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}
      <EpsfFormFillModal
        open={Boolean(formFillModal?.formInstanceId)}
        formInstanceId={formFillModal?.formInstanceId || ""}
        workItem={formFillModal?.workItem || null}
        mode={formFillModal?.mode || "fill"}
        onClose={() => setFormFillModal(null)}
        onSubmitted={async () => {
          setFormFillModal(null);
          refreshFormDues();
        }}
        onApproved={async () => {
          setFormFillModal(null);
          refreshFormDues();
        }}
      />
      <InfoAlert {...alertState} onClose={hideAlert} />
    </EpsfAccessGate>
  );
}
